import { Injectable } from '@angular/core';
import {
  IndicatorResponseDto,
  TrackingRegistryResponseDto,
  TrackingRegistryService,
  WalletTrackingRegistryResponseInnerOutputDto,
} from 'src/generated-sources/openapi/wallet-service/tracking-registry';
import { finalize, map, Observable } from 'rxjs';
import { NO_NAME } from 'src/app/components/constants';
import { TrackingRegistryRegisterDialogProps } from 'src/app/components/entity-manager/tracking-record-register-dialog.ts/tracking-record-register-dialog.component';
import { PageLoaderService } from 'src/app/services/page-loader.service';
import { dateToUserTimezone } from 'src/app/helpers/date-utils';
import { DateFormat } from 'src/app/shared/enums/date-formats.enum';

export type RestrictiveData = {
  name: string;
  quantity: number;
  totalValue: number;
  date: string;
};

export interface TrackingRegistryViewBase {
  id: string;
  registryId: string;
  name: string;
  document: string;
  wallets: WalletTrackingRegistryResponseInnerOutputDto[];
  internalCode: string;
  segment: string;
  score: number | null;
  restrictives: RestrictiveData[];
  totalRestrictive: number;
  inclusionDate: Date;
  isFavorite: boolean;
}

@Injectable()
export abstract class TrackingRegistryBaseFacade {
  constructor(
    protected readonly trackingRegistryService: TrackingRegistryService,
    protected readonly pageLoaderService: PageLoaderService
  ) {}

  public findByIdForEdit(
    id: string
  ): Observable<TrackingRegistryRegisterDialogProps> {
    this.pageLoaderService.setPageLoader(true);
    return this.trackingRegistryService.findById({ id }).pipe(
      map(this.transformToDialogProps),
      finalize(() => this.pageLoaderService.setPageLoader(false))
    );
  }

  public toggleFavorite(id: string) {
    this.pageLoaderService.setPageLoader(true);
    return this.trackingRegistryService
      .toggleFavorite({ id })
      .pipe(finalize(() => this.pageLoaderService.setPageLoader(false)));
  }

  public deleteRegistry(id: string, walletId?: string) {
    this.pageLoaderService.setPageLoader(true);
    return this.trackingRegistryService
      .deleteById({ id, walletId })
      .pipe(finalize(() => this.pageLoaderService.setPageLoader(false)));
  }

  public transformToView(
    dto: TrackingRegistryResponseDto
  ): TrackingRegistryViewBase {
    const restrictives = this.formatRestrictiveData(dto.indicators || []);
    const scoreValue = this.getScoreValue(dto.indicators || []);
    const totalRestrictive = restrictives.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    return {
      id: dto.id,
      registryId: dto.registryId,
      name: dto.name || NO_NAME,
      document: dto.document,
      wallets: dto.walletTrackingRegistries || [],
      internalCode: dto.internalCode || '-',
      segment: dto.segment || '-',
      score: scoreValue,
      restrictives,
      totalRestrictive,
      inclusionDate: new Date(dto.control.createdAt),
      isFavorite: dto.favorite,
    };
  }

  public getScoreValue(indicators: IndicatorResponseDto[]): number | null {
    if (!indicators) {
      return null;
    }

    const scoreIndicator = indicators.find((indicator) =>
      indicator.name.toLowerCase().includes('score')
    );

    return scoreIndicator ? parseInt(scoreIndicator.value) : null;
  }

  public formatRestrictiveData(
    indicators: IndicatorResponseDto[]
  ): RestrictiveData[] {
    if (!indicators) {
      return [];
    }

    const groupedData = new Map<string, RestrictiveData>();

    for (const indicator of indicators) {
      const baseName = indicator.name.split(' - ')[0];
      const isScore = baseName.toLowerCase().includes('score');

      if (!baseName || isScore) {
        continue;
      }

      if (!groupedData.has(baseName)) {
        groupedData.set(baseName, {
          name: baseName,
          quantity: 0,
          totalValue: 0,
          date: dateToUserTimezone(indicator.ingestionDate, DateFormat.DATE),
        });
      }

      const currentData = groupedData.get(baseName);
      const valueAsNumber = parseFloat(indicator.value);

      if (indicator.name.includes('Qtde')) {
        currentData.quantity = valueAsNumber;
      } else if (indicator.name.includes('Valores')) {
        currentData.totalValue = valueAsNumber;
      }
    }

    return Array.from(groupedData.values()).filter(
      (data) => data.quantity > 0 || data.totalValue > 0
    );
  }

  public transformToDialogProps(
    dto: TrackingRegistryResponseDto
  ): TrackingRegistryRegisterDialogProps {
    return {
      id: dto.id,
      document: dto.document,
      name: dto.name,
      creationDate: dateToUserTimezone(dto.control.createdAt, DateFormat.DATE),
      internalCode: dto.internalCode,
      segment: dto.segment,
      favorite: dto.favorite,
    };
  }
}
